'use client';

import type { AutoFixLoopApiSource } from '@/lib/auto-fix-loop/analytics';
import type { AutoFixLoopStatusView } from '@/lib/auto-fix-loop/status';
import { debounceStrategy, usePacedMutations } from '@tanstack/react-db';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  AUTO_FIX_LOOP_ROW_KEY,
  autoFixLoopStatusQueryKey,
  getAutoFixLoopCollection,
  postAutoFixLoopAction,
} from './auto-fix-loop-collection';

/**
 * The API answers an ineligible or unauthorized toggle with a message written
 * for the person clicking; keep it instead of a generic failure.
 */
export class AutoFixLoopToggleError extends Error {
  readonly code: string | null;
  /** The state the switch was being moved to when the request was refused. */
  readonly target: boolean;

  constructor(message: string, code: string | null, target: boolean) {
    super(message);
    this.name = 'AutoFixLoopToggleError';
    this.code = code;
    this.target = target;
  }
}

const TOGGLE_DEBOUNCE_MS = 250;

/**
 * The switch on cubic's PR page.
 *
 * Flipping it applies an optimistic overlay through a collection transaction,
 * so the switch takes the target state at once and stays live. Rapid flips
 * are debounced into a single request carrying the final target — a quick
 * on→off never leaves the server on. A refused request rolls the overlay back
 * to the synced state automatically (the switch snaps back), refetches the
 * status so an `eligibility` change lands, and exposes the refusal until the
 * next successful flip. Realtime refetches keep updating the synced layer
 * underneath without disturbing the pending overlay.
 *
 * The POST response is the fresh status without `eligibility` (an enabled loop
 * is eligible by construction), and with `allowance` only after a dispatch,
 * which may have counted the PR, so it is merged over the cached view rather
 * than replacing it.
 *
 * `toggle` resolves once the request settles: null on success, the refusal
 * otherwise. `source` defaults to the switch itself; a debounced burst reports
 * the source of its last flip.
 */
export function useAutoFixLoopToggle(owner: string, repo: string, number: string) {
  const queryClient = useQueryClient();
  const queryKey = useMemo(
    () => autoFixLoopStatusQueryKey(owner, repo, number),
    [owner, repo, number],
  );
  const collection = useMemo(
    () =>
      owner && repo && number ? getAutoFixLoopCollection(queryClient, owner, repo, number) : null,
    [owner, repo, number, queryClient],
  );
  const mountedRef = useRef(true);
  const inFlightRef = useRef(0);
  const sourceRef = useRef<AutoFixLoopApiSource>('pr_page');
  const [pendingTarget, setPendingTarget] = useState<boolean | null>(null);
  const [error, setError] = useState<AutoFixLoopToggleError | null>(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const mutate = usePacedMutations<{ target: boolean }, AutoFixLoopStatusView>({
    strategy: debounceStrategy({ wait: TOGGLE_DEBOUNCE_MS }),
    onMutate: ({ target }) => {
      collection?.update(AUTO_FIX_LOOP_ROW_KEY, (draft) => {
        draft.enabled = target;
      });
    },
    mutationFn: async ({ transaction }) => {
      const target = transaction.mutations.at(-1)!.modified.enabled;
      await queryClient.cancelQueries({ queryKey });
      const result = await postAutoFixLoopAction(owner, repo, number, {
        action: target ? 'enable' : 'disable',
        source: sourceRef.current,
      });
      if (!result.ok) {
        throw new AutoFixLoopToggleError(
          result.error ?? 'Auto-fix could not be updated.',
          result.code,
          target,
        );
      }
      const { data } = result;
      queryClient.setQueryData<AutoFixLoopStatusView>(queryKey, (previous) => ({
        ...previous,
        ...data,
        eligibility: data.eligibility ?? previous?.eligibility ?? { eligible: true, reason: null },
        allowance: data.allowance ?? previous?.allowance ?? null,
      }));
      return data;
    },
  });

  const toggle = useCallback(
    (
      target: boolean,
      source: AutoFixLoopApiSource = 'pr_page',
    ): Promise<AutoFixLoopToggleError | null> => {
      if (!collection?.has(AUTO_FIX_LOOP_ROW_KEY)) {
        return Promise.resolve(
          new AutoFixLoopToggleError('Auto-fix is not ready yet.', null, target),
        );
      }

      setError(null);
      inFlightRef.current += 1;
      setPendingTarget(target);
      sourceRef.current = source;

      const transaction = mutate({ target });
      return transaction.isPersisted.promise
        .then(
          () => null,
          (caught: unknown) => {
            const refusal =
              caught instanceof AutoFixLoopToggleError
                ? caught
                : new AutoFixLoopToggleError(
                    caught instanceof Error ? caught.message : 'Auto-fix could not be updated.',
                    null,
                    target,
                  );
            setError(refusal);
            void queryClient.invalidateQueries({ queryKey });
            return refusal;
          },
        )
        .finally(() => {
          inFlightRef.current = Math.max(0, inFlightRef.current - 1);
          if (mountedRef.current && inFlightRef.current === 0) {
            setPendingTarget(null);
          }
        });
    },
    [collection, mutate, queryClient, queryKey],
  );

  return { toggle, isPending: pendingTarget !== null, pendingTarget, error };
}
